// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Create the auth context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasValidReferralCode, setHasValidReferralCode] = useState(false);
  
  // Check if user has a referral code in Firestore and get updated profile info
  const fetchUserData = async (user) => {
    if (!user) return null;
    
    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check for referral code
        if (userData.referralCode) {
          setHasValidReferralCode(true);
          localStorage.setItem('referralCodeVerified', 'true');
        }
        
        // Create an enhanced user object with Firestore data
        const enhancedUser = {
          ...user,
          // If photoURL exists in Firestore, use it (it might be more up-to-date)
          photoURL: userData.photoURL || user.photoURL
        };
        
        return enhancedUser;
      }
      
      return user;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return user;
    }
  };
  
  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get enhanced user data with Firestore info
        const enhancedUser = await fetchUserData(user);
        setCurrentUser(enhancedUser);
      } else {
        setCurrentUser(null);
      }
      
      setLoading(false);
    });
    
    // Cleanup subscription
    return unsubscribe;
  }, []);
  
  // Store referral code verification in local storage to persist between refreshes
  useEffect(() => {
    const storedVerification = localStorage.getItem('referralCodeVerified');
    if (storedVerification === 'true') {
      setHasValidReferralCode(true);
    }
  }, []);
  
  // Set referral code verification
  const setReferralVerified = (value) => {
    setHasValidReferralCode(value);
    localStorage.setItem('referralCodeVerified', value.toString());
  };
  
  // Reset auth state (for logout)
  const resetAuthState = () => {
    setHasValidReferralCode(false);
    localStorage.removeItem('referralCodeVerified');
  };
  
  const value = {
    currentUser,
    loading,
    hasValidReferralCode,
    setReferralVerified,
    resetAuthState
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};